import { create } from 'zustand';
import {
  Notification,
  NotificationFilter,
  NotificationPreferences,
  ToastNotification,
  NotificationStats
} from '../types/notifications';

interface NotificationState {
  // State
  notifications: Notification[];
  toasts: ToastNotification[];
  preferences: NotificationPreferences;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  filter: NotificationFilter;

  // Stats
  stats: NotificationStats;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;

  // Toast actions
  showToast: (toast: Omit<ToastNotification, 'id'>) => void;
  removeToast: (id: string) => void;

  // Preferences
  updatePreferences: (preferences: Partial<NotificationPreferences>) => void;

  // Filter
  setFilter: (filter: NotificationFilter) => void;
  clearFilter: () => void;

  // WebSocket
  setConnectionStatus: (connected: boolean) => void;

  // Utilities
  updateStats: () => void;
  getFilteredNotifications: () => Notification[];
}

const defaultPreferences: NotificationPreferences = {
  email: true,
  push: true,
  inApp: true,
  sound: false,
  desktop: false,
};

const calculateStats = (notifications: Notification[]): NotificationStats => {
  const stats: NotificationStats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.read).length,
    byType: {
      info: 0,
      success: 0,
      warning: 0,
      error: 0,
    },
    byPriority: {
      low: 0,
      medium: 0,
      high: 0,
      urgent: 0,
    },
  };

  notifications.forEach(notification => {
    stats.byType[notification.type]++;
    stats.byPriority[notification.priority]++;
  });

  return stats;
};

export const useNotificationStore = create<NotificationState>((set, get) => ({
  // Initial state
  notifications: [],
  toasts: [],
  preferences: defaultPreferences,
  isConnected: false,
  isLoading: false,
  error: null,
  filter: {},
  stats: {
    total: 0,
    unread: 0,
    byType: { info: 0, success: 0, warning: 0, error: 0 },
    byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
  },

  // Add a new notification
  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };

    set((state) => {
      const updatedNotifications = [newNotification, ...state.notifications];
      return {
        notifications: updatedNotifications,
        stats: calculateStats(updatedNotifications),
      };
    });

    // Show toast for high priority notifications
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      get().showToast({
        type: notification.type,
        title: notification.title,
        message: notification.message,
        duration: 5000,
      });
    }

    // Play sound if enabled
    if (get().preferences.sound && notification.priority !== 'low') {
      playNotificationSound();
    }
  },

  // Remove a notification
  removeNotification: (id) =>
    set((state) => {
      const updatedNotifications = state.notifications.filter((n) => n.id !== id);
      return {
        notifications: updatedNotifications,
        stats: calculateStats(updatedNotifications),
      };
    }),

  // Mark notification as read
  markAsRead: (id) =>
    set((state) => {
      const updatedNotifications = state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      );
      return {
        notifications: updatedNotifications,
        stats: calculateStats(updatedNotifications),
      };
    }),

  // Mark all notifications as read
  markAllAsRead: () =>
    set((state) => {
      const updatedNotifications = state.notifications.map((n) => ({ ...n, read: true }));
      return {
        notifications: updatedNotifications,
        stats: calculateStats(updatedNotifications),
      };
    }),

  // Clear all notifications
  clearNotifications: () =>
    set({
      notifications: [],
      stats: {
        total: 0,
        unread: 0,
        byType: { info: 0, success: 0, warning: 0, error: 0 },
        byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
      },
    }),

  // Show toast notification
  showToast: (toast) => {
    const newToast: ToastNotification = {
      ...toast,
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      duration: toast.duration || 3000,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove toast after duration
    setTimeout(() => {
      get().removeToast(newToast.id);
    }, newToast.duration);
  },

  // Remove toast
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  // Update preferences
  updatePreferences: (preferences) =>
    set((state) => ({
      preferences: { ...state.preferences, ...preferences },
    })),

  // Set filter
  setFilter: (filter) => set({ filter }),

  // Clear filter
  clearFilter: () => set({ filter: {} }),

  // Set connection status
  setConnectionStatus: (connected) => set({ isConnected: connected }),

  // Update stats
  updateStats: () =>
    set((state) => ({
      stats: calculateStats(state.notifications),
    })),

  // Get filtered notifications
  getFilteredNotifications: () => {
    const state = get();
    let filtered = [...state.notifications];

    if (state.filter.types?.length) {
      filtered = filtered.filter((n) => state.filter.types!.includes(n.type));
    }

    if (state.filter.priorities?.length) {
      filtered = filtered.filter((n) => state.filter.priorities!.includes(n.priority));
    }

    if (state.filter.read !== undefined) {
      filtered = filtered.filter((n) => n.read === state.filter.read);
    }

    if (state.filter.source) {
      filtered = filtered.filter((n) => n.source === state.filter.source);
    }

    if (state.filter.startDate) {
      filtered = filtered.filter((n) => n.timestamp >= state.filter.startDate!);
    }

    if (state.filter.endDate) {
      filtered = filtered.filter((n) => n.timestamp <= state.filter.endDate!);
    }

    return filtered;
  },
}));

// Helper function to play notification sound
const playNotificationSound = () => {
  const audio = new Audio('/notification-sound.mp3');
  audio.volume = 0.5;
  audio.play().catch(console.error);
};